import { useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

import { cn } from '../../lib/cn';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { IconButton } from '../IconButton';
import styles from './Drawer.module.css';

export type DrawerSide = 'right';

export type DrawerProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly side?: DrawerSide;
  readonly width?: number;
  readonly title: string;
  readonly header?: ReactNode;
  readonly children: ReactNode;
  readonly ariaLabel?: string;
  /** Значение `data-testid` для e2e; по умолчанию `drawer`. */
  readonly testId?: string;
};

/** Боковая панель задачи: 560 px по умолчанию (docs/06 §2). */
export function Drawer({
  open,
  onClose,
  side = 'right',
  width,
  title,
  header,
  children,
  ariaLabel,
  testId = 'drawer',
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(panelRef, open, onClose);

  if (!open) {
    return null;
  }

  return createPortal(
    <div className={styles['overlay']} onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? title}
        data-testid={testId}
        tabIndex={-1}
        className={cn(styles['panel'], styles[side])}
        style={width === undefined ? undefined : { width: `${width}px` }}
      >
        <header className={styles['header']}>
          {header ?? <h2 className={styles['title']}>{title}</h2>}
          <IconButton label="Закрыть" icon={<X size={18} />} onClick={onClose} />
        </header>
        <div className={styles['body']}>{children}</div>
      </aside>
    </div>,
    document.body,
  );
}
